import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { listDepartmentTeams, removeDepartmentTeam, setDepartmentTeam } from '@/api/onsiteLiveTeams';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { DepartmentTeam, OnsiteLiveTeam } from '@/types/oscar';
import { ErrorBanner } from '../BranchRelationships/shared';
import { inputCls } from '../BranchRelationships/styles';
import { DEPARTMENT_TEAMS_KEY, RULE_KEY, TEAMS_KEY } from './keys';
import { DEFAULT_TEAM_ID } from './schema';

/** A drop waiting for the admin to confirm it. `toTeamId` null = back to Default. */
type PendingMove = { dept: DepartmentTeam; toTeamId: number | null };

/**
 * Departments → teams (oscar.DepartmentTeam, the D13 rule). The same board as the
 * Position Title Allocator, one level up: a job title puts a person in a
 * department; the department puts them in a team. Left: every active team (not
 * Default) as a drop target. Right: the departments with no team, which fall to
 * team 15 Default (CatchAll) and get no approval authority.
 *
 * A mapping changes nobody by itself: people move team when the rule is applied
 * (Rule preview tab, or the 15-minute sync once it is switched on). Every drop is
 * confirmed, saying how many people it will move.
 */
export function DepartmentTeamsTab({ teams, canMap }: { teams: OnsiteLiveTeam[]; canMap: boolean }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<PendingMove | null>(null);

  const query = useQuery({ queryKey: DEPARTMENT_TEAMS_KEY, queryFn: ({ signal }) => listDepartmentTeams(signal) });
  const departments = useMemo(() => query.data ?? [], [query.data]);

  const move = useMutation({
    mutationFn: ({ dept, toTeamId }: PendingMove) =>
      toTeamId === null ? removeDepartmentTeam(dept.DepartmentCode) : setDepartmentTeam(dept.DepartmentCode, toTeamId),
    onSuccess: () => {
      setPending(null);
      void qc.invalidateQueries({ queryKey: DEPARTMENT_TEAMS_KEY });
      void qc.invalidateQueries({ queryKey: TEAMS_KEY });
      void qc.invalidateQueries({ queryKey: RULE_KEY });
    },
  });

  const targets = useMemo(() => teams.filter((t) => t.IsActive && t.TeamId !== DEFAULT_TEAM_ID), [teams]);
  const teamName = (id: number | null) =>
    id === null ? 'Default (CatchAll)' : teams.find((t) => t.TeamId === id)?.TeamCode ?? `Team ${id}`;

  const term = search.trim().toLowerCase();
  const matches = (d: DepartmentTeam) => !term || `${d.DepartmentCode} ${d.Description} ${d.BusinessUnit ?? ''}`.toLowerCase().includes(term);
  const byTeam = useMemo(() => {
    const map = new Map<number, DepartmentTeam[]>();
    for (const d of departments) {
      if (d.TeamId === null) continue;
      map.set(d.TeamId, [...(map.get(d.TeamId) ?? []), d]);
    }
    return map;
  }, [departments]);
  const targetIds = new Set(targets.map((t) => t.TeamId));
  // Not mapped, or mapped to a team that is no longer a target (inactive): both shown in the tray.
  const unmapped = departments.filter((d) => d.TeamId === null || !targetIds.has(d.TeamId));

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const onDragEnd = (e: DragEndEvent) => {
    const over = e.over?.data.current as { teamId: number | null } | undefined;
    const dept = e.active.data.current?.dept as DepartmentTeam | undefined;
    if (!over || !dept || over.teamId === dept.TeamId) return;
    move.reset();
    setPending({ dept, toTeamId: over.teamId });
  };

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div>
      <p className="mb-3 text-sm text-darkGrey/80">
        {canMap
          ? 'Drag a department onto a team. Keyboard: focus a department, press Space, arrow to a team, Space to drop. People move when the rule is applied (Rule preview).'
          : 'Which team each department puts its people in.'}
      </p>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search departments
          <input aria-label="Search departments" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code, description or business unit" className={`${inputCls} mt-1 font-normal`} />
        </label>
        <div className="flex gap-2 pb-0.5 text-xs">
          <button type="button" onClick={() => setExpanded(new Set(targets.map((t) => t.TeamId)))} className="rounded border border-border bg-white px-2 py-1.5 font-medium text-darkGrey hover:bg-bg">Expand all</button>
          <button type="button" onClick={() => setExpanded(new Set())} className="rounded border border-border bg-white px-2 py-1.5 font-medium text-darkGrey hover:bg-bg">Collapse all</button>
        </div>
      </div>

      {query.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
      {query.isError && <ErrorBanner message={(query.error as Error).message || 'Could not load the departments.'} />}

      {!query.isLoading && !query.isError && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            <div className="flex-1 space-y-2" aria-label="Teams">
              {targets.map((t) => {
                const depts = (byTeam.get(t.TeamId) ?? []).filter(matches);
                return (
                  <TeamRow
                    key={t.TeamId}
                    team={t}
                    departments={depts}
                    total={byTeam.get(t.TeamId)?.length ?? 0}
                    expanded={expanded.has(t.TeamId) || (term !== '' && depts.length > 0)}
                    onToggle={() => toggle(t.TeamId)}
                    canMap={canMap}
                  />
                );
              })}
            </div>
            <aside className="w-full shrink-0 lg:sticky lg:top-4 lg:w-80">
              <DefaultTray departments={unmapped.filter(matches)} total={unmapped.length} canMap={canMap} />
            </aside>
          </div>
        </DndContext>
      )}

      {pending && (
        <AlertDialog
          open
          onOpenChange={(o) => { if (!o) setPending(null); }}
          title={`Move ${pending.dept.DepartmentCode} to ${teamName(pending.toTeamId)}?`}
          description={
            <>
              {pending.dept.DepartmentCode} ({pending.dept.Description}) goes from <b>{teamName(pending.dept.TeamId)}</b> to{' '}
              <b>{teamName(pending.toTeamId)}</b>.{' '}
              {pending.dept.People > 0
                ? `${pending.dept.People} active user(s) hold this department. Their team, and so their approval limits, change when the rule is applied.`
                : 'Nobody active holds this department yet.'}
              {pending.toTeamId === null && ' Default (CatchAll) has no approval authority.'}
            </>
          }
          confirmLabel="Move"
          closeOnConfirm={false}
          onConfirm={() => move.mutate(pending)}
        >
          {move.isError && (
            <p role="alert" className="text-sm text-red">
              {move.error instanceof ApiError ? move.error.message : 'Could not move that department.'}
            </p>
          )}
        </AlertDialog>
      )}
    </div>
  );
}

function TeamRow({
  team, departments, total, expanded, onToggle, canMap,
}: {
  team: OnsiteLiveTeam;
  departments: DepartmentTeam[];
  total: number;
  expanded: boolean;
  onToggle: () => void;
  canMap: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `team-${team.TeamId}`, data: { teamId: team.TeamId } });
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <div ref={setNodeRef} className={`rounded border bg-white shadow-sm transition-colors ${isOver ? 'border-orange ring-2 ring-orange/40' : 'border-border'}`}>
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-[#F4F6F9]">
        <Chevron className="h-4 w-4 shrink-0 text-darkGrey/50" aria-hidden />
        <span className="font-mono text-xs text-darkGrey/60">{team.TeamId}</span>
        <span className="text-sm font-semibold text-navy">{team.TeamCode}</span>
        {team.OLT && <span className="rounded-full bg-navy/10 px-1.5 text-[10px] font-semibold text-navy">OLT</span>}
        <span className="ml-auto shrink-0 text-xs text-darkGrey/60">{team.ActiveUsers} users</span>
        <span className="shrink-0 rounded-full bg-[#E6ECF2] px-2 py-0.5 text-xs font-semibold text-navy" title="Departments mapped">{total}</span>
      </button>
      {expanded && (
        <ul className="space-y-1.5 border-t border-border px-3 py-2">
          {departments.map((d) => <DepartmentChip key={d.DepartmentCode} dept={d} canMap={canMap} />)}
          {departments.length === 0 && <li className="text-xs text-darkGrey/50">No departments{total > 0 ? ' match the search' : ''}.</li>}
        </ul>
      )}
    </div>
  );
}

function DefaultTray({ departments, total, canMap }: { departments: DepartmentTeam[]; total: number; canMap: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'team-default', data: { teamId: null } });
  return (
    <div ref={setNodeRef} className={`rounded border border-dashed p-3 transition-colors ${isOver ? 'border-orange bg-orange/5 ring-2 ring-orange/40' : 'border-border bg-[#F4F6F9]'}`}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Not mapped — Default (CatchAll) <span className="text-darkGrey/40">({total})</span>
      </h2>
      <p className="mt-0.5 text-xs text-darkGrey/60">
        No approval authority.{canMap ? ' Drag a department here to take its team away.' : ''}
      </p>
      <ul className="mt-2 max-h-[65vh] space-y-1.5 overflow-y-auto">
        {departments.map((d) => <DepartmentChip key={d.DepartmentCode} dept={d} canMap={canMap} />)}
        {departments.length === 0 && <li className="text-sm text-darkGrey/60">None{total > 0 ? ' match the search' : ''}.</li>}
      </ul>
    </div>
  );
}

function DepartmentChip({ dept, canMap }: { dept: DepartmentTeam; canMap: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `dept-${dept.DepartmentCode}`,
    data: { dept },
    disabled: !canMap,
  });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;
  return (
    <li ref={setNodeRef} style={style} className={`flex items-center gap-2 rounded border border-border bg-white px-2 py-1 text-sm ${isDragging ? 'opacity-50' : ''}`}>
      {canMap && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag ${dept.DepartmentCode}`}
          className="cursor-grab touch-none text-darkGrey/40 hover:text-darkGrey focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      )}
      <span className="font-mono text-xs font-semibold text-navy">{dept.DepartmentCode}</span>
      <span className="min-w-0 flex-1 truncate text-xs text-darkGrey/80" title={dept.Description}>{dept.Description}</span>
      <span className="shrink-0 text-xs text-darkGrey/60" title="Active users with this department">{dept.People}</span>
    </li>
  );
}
